/**
 * Left-click-to-pick for the vector drawing tools, without dropping
 * vertices while the user orbits the camera.
 *
 * The browser's `click` event is no good here: it fires whenever
 * `pointerdown` and `pointerup` land on the same element, however far the
 * pointer travelled in between. So every left-drag orbit that ended on the
 * canvas dropped a vertex where the rotation stopped, and a bare `pointerup`
 * listener has the same problem.
 *
 * A gesture counts as a pick only when:
 *   - it is a primary-button, single-pointer press (a second finger touching
 *     down turns it into a pinch/pan and cancels the pick);
 *   - the pointer never strayed `PICK_DRAG_THRESHOLD_PX` from the press point
 *     at any time during the press — tracking the *maximum* excursion, not
 *     just the release position, so an orbit that swings back to where it
 *     started is still recognised as an orbit;
 *   - it is not the second click of a double-click: a pick within
 *     `DOUBLE_CLICK_WINDOW_MS` and `DOUBLE_CLICK_RADIUS_PX` of the previous
 *     accepted pick is swallowed, so double-click-to-finish doesn't stack a
 *     duplicate vertex on top of the last one. Native `dblclick` still fires,
 *     so the tools' finalize handlers are unaffected.
 *
 * Picks are decided on the element itself (not `window`) so `onPick` runs in the same
 * slot the old canvas `click`/`pointerup` listeners did — before the vertex
 * drag handlers' `window` `pointerup`, which the tools rely on.
 */
export const PICK_DRAG_THRESHOLD_PX = 5;
export const DOUBLE_CLICK_WINDOW_MS = 400;
export const DOUBLE_CLICK_RADIUS_PX = 8;

export function attachLeftClickPick(el: HTMLElement, onPick: (e: PointerEvent) => void): () => void {
  let press: { id: number; x: number; y: number; maxDist: number } | null = null;
  let lastPick: { x: number; y: number; t: number } | null = null;
  const activePointers = new Set<number>();

  // jsdom (tests) dispatches plain MouseEvents without pointerId/isPrimary.
  const idOf = (e: PointerEvent) => e.pointerId ?? 0;

  const onDown = (e: PointerEvent) => {
    activePointers.add(idOf(e));
    if (activePointers.size > 1 || e.isPrimary === false || e.button !== 0) {
      press = null;
      return;
    }
    press = { id: idOf(e), x: e.clientX, y: e.clientY, maxDist: 0 };
  };

  const onMove = (e: PointerEvent) => {
    if (!press || idOf(e) !== press.id) return;
    const d = Math.hypot(e.clientX - press.x, e.clientY - press.y);
    if (d > press.maxDist) press.maxDist = d;
  };

  const onUp = (e: PointerEvent) => {
    activePointers.delete(idOf(e));
    if (!press || e.button !== 0 || idOf(e) !== press.id) return;
    const p = press;
    press = null;
    const dist = Math.max(p.maxDist, Math.hypot(e.clientX - p.x, e.clientY - p.y));
    if (dist >= PICK_DRAG_THRESHOLD_PX) return;

    const now = Date.now();
    if (
      lastPick &&
      now - lastPick.t < DOUBLE_CLICK_WINDOW_MS &&
      Math.hypot(e.clientX - lastPick.x, e.clientY - lastPick.y) < DOUBLE_CLICK_RADIUS_PX
    ) {
      // Second half of a double-click. Clear it so a triple-click doesn't
      // chain into a third rejection off the same anchor.
      lastPick = null;
      return;
    }
    lastPick = { x: e.clientX, y: e.clientY, t: now };
    onPick(e);
  };

  const onCancel = (e: PointerEvent) => {
    activePointers.delete(idOf(e));
    if (press && idOf(e) === press.id) press = null;
  };

  // A touch lifted off-canvas never reaches `el` — forget it on `window` so a
  // stale id can't make every later press look like a multi-touch.
  const onWindowRelease = (e: PointerEvent) => {
    if (e.target !== el) activePointers.delete(idOf(e));
  };

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onCancel);
  window.addEventListener('pointerup', onWindowRelease);
  window.addEventListener('pointercancel', onWindowRelease);
  return () => {
    window.removeEventListener('pointerup', onWindowRelease);
    window.removeEventListener('pointercancel', onWindowRelease);
    el.removeEventListener('pointerdown', onDown);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onCancel);
  };
}
